import React from "react";
import { Trash2, X } from "lucide-react";
import { STROKE_IDS, SHAPE_IDS } from "@/data/printGeometry";

export const inp =
  "w-full rounded-xl border-2 border-[#E5DECF] bg-white px-3 py-2 font-body text-[#1F2937] outline-none focus:border-[#0D9488]";
export const GRADES = ["PreK", "Kindergarten", "Grade 1", "Grade 2", "Grade 3", "Grade 4", "Grade 5", "Grade 6", "Grade 7", "Grade 8"];
export const ICONS = ["star", "apple", "leaf", "fish", "circle", "mountain", "sailboat", "key", "sun", "trees", "waves", "snowflake"];

export const blankConfig = (engine) => ({
  mcq: { question: "", options: ["", "", ""], answer: 0, speak: true },
  match: { leftLabel: "Item", rightLabel: "Match", speak: true, pairs: [{ left: "", right: "" }, { left: "", right: "" }] },
  count: { a: 3, b: 2, icon: "apple", stage: "add", answer: 5, target: 3, options: [4, 5, 6] },
  phonics: { word: "", sounds: [{ g: "", say: "" }], mode: "explore", options: ["", "", ""], answer: 0 },
  sink_float: { speak: true, items: [{ name: "", icon: "circle", floats: true }] },
  trace: { kind: "letter", text: "A", style: "print" },
}[engine]);

const TRACE_KINDS = [["stroke", "Pre-writing stroke"], ["shape", "Shape"], ["letter", "Letter"], ["word", "Word"], ["sentence", "Sentence"]];

export function ActivityEditor({ act, onChange, onRemove, idx }) {
  const c = act.config;
  const setC = (patch) => onChange({ ...act, config: { ...c, ...patch } });
  return (
    <div className="rounded-2xl border border-[#E5DECF] bg-[#FAF7F2] p-4" data-testid={`activity-editor-${idx}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="rounded-full bg-[#0D9488]/10 px-2.5 py-1 text-xs font-bold text-[#0D9488]">{act.engine}</span>
          <input value={act.title} onChange={(e) => onChange({ ...act, title: e.target.value })} placeholder="Activity title" className="rounded-lg border border-[#E5DECF] px-2 py-1 text-sm font-semibold" />
        </div>
        <button onClick={onRemove} className="text-[#E05A47] hover:opacity-70" data-testid={`remove-activity-${idx}`}><Trash2 size={16} /></button>
      </div>
      <input value={act.prompt} onChange={(e) => onChange({ ...act, prompt: e.target.value })} placeholder="Prompt / instruction shown to the child" className={`${inp} mt-3`} />

      {act.engine === "mcq" && (
        <div className="mt-3 space-y-2">
          <input value={c.question} onChange={(e) => setC({ question: e.target.value })} placeholder="Question" className={inp} />
          {c.options.map((o, i) => (
            <div key={i} className="flex items-center gap-2">
              <input type="radio" name={`ans-${idx}`} checked={c.answer === i} onChange={() => setC({ answer: i })} />
              <input value={o} onChange={(e) => { const opts = [...c.options]; opts[i] = e.target.value; setC({ options: opts }); }} placeholder={`Option ${i + 1}${c.answer === i ? " (correct)" : ""}`} className={inp} />
              {c.options.length > 2 && <button onClick={() => setC({ options: c.options.filter((_, j) => j !== i), answer: 0 })} className="text-[#E05A47]"><X size={14} /></button>}
            </div>
          ))}
          {c.options.length < 4 && <button onClick={() => setC({ options: [...c.options, ""] })} className="text-sm font-bold text-[#0D9488]">+ Add option</button>}
        </div>
      )}

      {act.engine === "match" && (
        <div className="mt-3 space-y-2">
          <div className="flex gap-2">
            <input value={c.leftLabel} onChange={(e) => setC({ leftLabel: e.target.value })} placeholder="Left label" className={inp} />
            <input value={c.rightLabel} onChange={(e) => setC({ rightLabel: e.target.value })} placeholder="Right label" className={inp} />
          </div>
          {c.pairs.map((p, i) => (
            <div key={i} className="flex items-center gap-2">
              <input value={p.left} onChange={(e) => { const pp = [...c.pairs]; pp[i] = { ...pp[i], left: e.target.value }; setC({ pairs: pp }); }} placeholder="Left" className={inp} />
              <span className="text-[#94A3B8]">↔</span>
              <input value={p.right} onChange={(e) => { const pp = [...c.pairs]; pp[i] = { ...pp[i], right: e.target.value }; setC({ pairs: pp }); }} placeholder="Right" className={inp} />
              {c.pairs.length > 2 && <button onClick={() => setC({ pairs: c.pairs.filter((_, j) => j !== i) })} className="text-[#E05A47]"><X size={14} /></button>}
            </div>
          ))}
          <button onClick={() => setC({ pairs: [...c.pairs, { left: "", right: "" }] })} className="text-sm font-bold text-[#0D9488]">+ Add pair</button>
        </div>
      )}

      {act.engine === "count" && (
        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
          <label className="text-sm">Group A<input type="number" value={c.a} onChange={(e) => setC({ a: +e.target.value })} className={inp} /></label>
          <label className="text-sm">Group B<input type="number" value={c.b} onChange={(e) => setC({ b: +e.target.value })} className={inp} /></label>
          <label className="text-sm">Icon<select value={c.icon} onChange={(e) => setC({ icon: e.target.value })} className={inp}>{ICONS.map((i) => <option key={i}>{i}</option>)}</select></label>
          <label className="text-sm">Mode<select value={c.stage} onChange={(e) => setC({ stage: e.target.value })} className={inp}><option value="add">Add both groups</option><option value="count">Count group A</option></select></label>
          <label className="text-sm">{c.stage === "add" ? "Answer" : "Count target"}<input type="number" value={c.stage === "add" ? c.answer : c.target} onChange={(e) => setC(c.stage === "add" ? { answer: +e.target.value } : { target: +e.target.value })} className={inp} /></label>
          <label className="text-sm col-span-2 sm:col-span-1">Options (comma)<input value={c.options.join(",")} onChange={(e) => setC({ options: e.target.value.split(",").map((x) => +x.trim()).filter((x) => !isNaN(x)) })} className={inp} /></label>
        </div>
      )}

      {act.engine === "phonics" && (
        <div className="mt-3 space-y-2">
          <input value={c.word} onChange={(e) => setC({ word: e.target.value })} placeholder="Word (e.g. cat)" className={inp} />
          <div className="text-xs font-bold text-[#6B7280]">Sounds (letter → how to say it)</div>
          {c.sounds.map((s, i) => (
            <div key={i} className="flex items-center gap-2">
              <input value={s.g} onChange={(e) => { const ss = [...c.sounds]; ss[i] = { ...ss[i], g: e.target.value }; setC({ sounds: ss }); }} placeholder="c" className={inp} />
              <input value={s.say} onChange={(e) => { const ss = [...c.sounds]; ss[i] = { ...ss[i], say: e.target.value }; setC({ sounds: ss }); }} placeholder="kuh" className={inp} />
              {c.sounds.length > 1 && <button onClick={() => setC({ sounds: c.sounds.filter((_, j) => j !== i) })} className="text-[#E05A47]"><X size={14} /></button>}
            </div>
          ))}
          <button onClick={() => setC({ sounds: [...c.sounds, { g: "", say: "" }] })} className="text-sm font-bold text-[#0D9488]">+ Add sound</button>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={c.mode === "blend"} onChange={(e) => setC({ mode: e.target.checked ? "blend" : "explore" })} /> Blend mode (pick the word)</label>
          {c.mode === "blend" && (
            <div className="space-y-2">
              {c.options.map((o, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input type="radio" name={`pans-${idx}`} checked={c.answer === i} onChange={() => setC({ answer: i })} />
                  <input value={o} onChange={(e) => { const opts = [...c.options]; opts[i] = e.target.value; setC({ options: opts }); }} placeholder={`Word ${i + 1}`} className={inp} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {act.engine === "trace" && (
        <div className="mt-3 grid gap-2 sm:grid-cols-3" data-testid={`trace-editor-${idx}`}>
          <select value={c.kind || "letter"} onChange={(e) => setC({ kind: e.target.value, text: e.target.value === "stroke" ? STROKE_IDS[0] : e.target.value === "shape" ? SHAPE_IDS[0] : "" })} className={inp}>
            {TRACE_KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          {c.kind === "stroke" || c.kind === "shape" ? (
            <select value={c.text || ""} onChange={(e) => setC({ text: e.target.value })} className={inp}>
              {(c.kind === "stroke" ? STROKE_IDS : SHAPE_IDS).map((x) => <option key={x} value={x}>{x.replace(/_/g, " ")}</option>)}
            </select>
          ) : (
            <input value={c.text || ""} onChange={(e) => setC({ text: e.target.value })} placeholder={c.kind === "letter" ? "One letter, e.g. A" : c.kind === "word" ? "Word, e.g. cat" : "Short sentence"} className={inp} />
          )}
          <select value={c.style || "print"} onChange={(e) => setC({ style: e.target.value })} className={inp}>
            <option value="print">Print letters</option>
            <option value="cursive">Cursive letters</option>
          </select>
        </div>
      )}

      {act.engine === "sink_float" && (
        <div className="mt-3 space-y-2">
          {c.items.map((it, i) => (
            <div key={i} className="flex items-center gap-2">
              <input value={it.name} onChange={(e) => { const its = [...c.items]; its[i] = { ...its[i], name: e.target.value }; setC({ items: its }); }} placeholder="Object name" className={inp} />
              <select value={it.icon} onChange={(e) => { const its = [...c.items]; its[i] = { ...its[i], icon: e.target.value }; setC({ items: its }); }} className={inp}>{ICONS.map((x) => <option key={x}>{x}</option>)}</select>
              <label className="flex items-center gap-1 text-sm whitespace-nowrap"><input type="checkbox" checked={it.floats} onChange={(e) => { const its = [...c.items]; its[i] = { ...its[i], floats: e.target.checked }; setC({ items: its }); }} /> Floats</label>
              {c.items.length > 1 && <button onClick={() => setC({ items: c.items.filter((_, j) => j !== i) })} className="text-[#E05A47]"><X size={14} /></button>}
            </div>
          ))}
          <button onClick={() => setC({ items: [...c.items, { name: "", icon: "circle", floats: true }] })} className="text-sm font-bold text-[#0D9488]">+ Add object</button>
        </div>
      )}
    </div>
  );
}
